
import { LoadingOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, message, Spin, Tag } from "antd";
import { useEffect, useState } from "react";
import { Draggable } from "react-drag-reorder";
import Dropzone from "react-dropzone";
import { AiOutlineDelete } from "react-icons/ai";
import { CgReorder } from "react-icons/cg";
import { FaRegCheckCircle } from "react-icons/fa";
import { FiUploadCloud } from "react-icons/fi";
import { IoNewspaperOutline, IoReaderOutline } from "react-icons/io5";
import { MdOutlineBrokenImage } from "react-icons/md";
import CanvasModal from "./canvasModal";
import UpdateImage from "./updateImage";
import { uploadImageToS3 } from "../../../config/s3config/s3fileUpload";
import { optimizePdfToImages } from "../helpers/compressUpload";
import { getUploadConfig } from "../../../config/uploadConfig";

const PDFJS = window.pdfjsLib;

function PagesList(props: any) {
  const [isloading, setIsloading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [item, setItem] = useState({}) as any;
  const [updateImage, setUpdateImage] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [currentIndex, setCurrentIndex] = useState<any>(null);

  useEffect(() => {
    setIsloading(true);
    setTimeout(() => {
      setIsloading(false);
    }, 50);
  }, [props?.uploadIndx]);

  const convertPdfToImage = async (file: File): Promise<string[]> => {
    try {
      // Use the optimized PDF processing function with progress tracking
      const maxConcurrency = getUploadConfig('PDF.MAX_CONCURRENCY') || 3;
      const imageUrls = await optimizePdfToImages(file, maxConcurrency, (progress, status) => {
        setUploadProgress(progress);
        setUploadStatus(status);
      });
      
      setUploadProgress(100);
      setUploadStatus("PDF processing completed!");
      
      console.log("Generated optimized S3 imageUrls:", imageUrls);
      return imageUrls || [];
    } catch (error) {
      console.error("PDF conversion or S3 upload error:", error);
      message.error("Failed to convert PDF to images or upload to S3");
      setUploadStatus("Upload failed");
      return [];
    }
  };

  const createObject = async (value: any) => {
    setIsloading(true);
    setUploadProgress(0);
    setUploadStatus("");
    let arro = props?.pages?.length ? [...props?.pages] : [];
    try {
      for (let i = 0; i < value?.length; i++) {
        const file = value[i];
        let imageUrls: string[] = [];

        if (file.type === "application/pdf") {
          const pdfImageUrls = await convertPdfToImage(file);
          imageUrls = pdfImageUrls || [];
        } else {
          // Validate file type
          const allowedTypes = ["image/jpeg", "image/png"];
          if (!allowedTypes.includes(file.type)) {
            message.error("File Format Not Supported. Use JPEG or PNG only.");
            continue;
          }

          // Upload image to S3
          const uploadResponse = await uploadImageToS3(file);
          const s3ImageUrl = uploadResponse.data?.data;

          if (!s3ImageUrl) {
            throw new Error("S3 upload failed for image");
          }

          imageUrls = [s3ImageUrl];
        }

        // Create an object for each page/image
        imageUrls.forEach((imageUrl, pageIndex) => {
          let obj = {
            index: arro?.length ? Number(arro?.length) : 0,
            fileUrl: imageUrl,
            file: file,
            name:
              file.type === "application/pdf" ? `${file?.name}` : file?.name,
            uploaded: true,
            coords: [],
            imageUrl: imageUrl,
          };
          arro.push(obj);
        });
      }
      props?.onChange(arro);
    } catch (error) {
      console.error("Error in createObject:", error);
      message.error("Failed to process or upload file");
    } finally {
      setIsloading(false);
    }
  };

  const FilePicked = async (value: any) => {
    console.log("Files received:", value);
    if (value?.length) {
      // Check file sizes
      const maxFileSize = getUploadConfig('PDF.MAX_FILE_SIZE_MB') || 50;
      const largeFiles = value.filter((file: File) => file.size > maxFileSize * 1024 * 1024);
      
      if (largeFiles.length > 0) {
        message.warning(`Large files detected (${largeFiles.length} files > ${maxFileSize}MB). Upload may take longer.`);
      }
      
      await createObject(value);
    } else {
      setIsloading(false);
      message.error("No file Found");
    }
  };

  const removeItem = (item: any, index: number) => {
    try {
      setIsloading(true);
      let arro = [...props?.pages];
      arro.splice(index, 1);
      props?.onChange(arro);
      setTimeout(() => {
        setIsloading(false);
      }, 50);
    } catch (err) {
      props?.onChange(props?.pages);
    }
  };

  const uploadImage = (val: any, index: any) => {
    try {
      setUpdateImage(false);
      setIsloading(true);
      let arro = [...props?.pages];
      const foundIndex = arro.findIndex((item: any) => item.index === index);
      if (foundIndex !== -1) {
        arro[foundIndex] = { ...arro[foundIndex], imageUrl: val };
        props?.onChange(arro);
      }
      setTimeout(() => {
        setIsloading(false);
      }, 50);
    } catch (err) {
      console.log("err", err);
    }
  };

  const getChangedPos = (currentPos: any, newPos: any) => {
    try {
      let arro = [...props?.pages];
      const updatedItems = [...arro];
      const movedItem = updatedItems.splice(currentPos, 1)[0];
      updatedItems.splice(newPos, 0, movedItem);
      props?.onChange(updatedItems);
    } catch (err) {
      console.log("err", err);
    }
  };

  const openModal = (item: any, index: any) => {
    if (item?.imageUrl) {
      setModalImage(item.imageUrl);
      setModalVisible(true);
      setCurrentIndex(index);
    } else {
      message.error("No image available for this page");
    }
  };

  const onSaveCanvas = (rectangles: any) => {
    try {
      if (currentIndex !== null) {
        let arro = [...props?.pages];
        const foundIndex = arro.findIndex(
          (item: any, index: any) => index === currentIndex
        );
        if (foundIndex !== -1) {
          arro[foundIndex] = { ...arro[foundIndex], coords: rectangles };
          props?.onChange(arro);
        }
      }
      setModalVisible(false);
    } catch (err) {
      console.log("err", err);
      setModalVisible(false);
    }
  };

  return (
    <>
      <label className="newsFormLabels">Upload Pages</label>
      <Dropzone
        onDrop={FilePicked}
        accept={{
          "image/jpeg": [".jpeg", ".jpg"],
          "image/png": [".png"],
          "application/pdf": [".pdf"],
        }}
      >
        {({ getRootProps, getInputProps }) => (
          <section {...getRootProps({})}>
            <input {...getInputProps()} />
            <Button
              icon={<UploadOutlined color="blue" size={30} />}
              block
              size="large"
              style={{
                border: "2px dashed #d1d1d1",
                height: "18vh",
                backgroundColor: "#fafafa",
                fontSize: 14,
              }}
            >
              Select or Drag Files
            </Button>
          </section>
        )}
      </Dropzone>
      <div style={{ margin: 20 }} />
      <label className="newsFormLabels">Pages</label>
      <div>
        {isloading ? (
          <div style={{ height: 500, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Spin
              indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
            {uploadStatus && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <div style={{ marginBottom: 8, color: '#666' }}>{uploadStatus}</div>
                {uploadProgress > 0 && uploadProgress < 100 && (
                  <div style={{ width: 200, height: 4, backgroundColor: '#f0f0f0', borderRadius: 2 }}>
                    <div 
                      style={{ 
                        width: `${uploadProgress}%`, 
                        height: '100%', 
                        backgroundColor: '#1890ff', 
                        borderRadius: 2,
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        ) : props?.pages?.length ? (
          <div>
            <Draggable onPosChange={getChangedPos}>
              {props?.pages?.map((item: any, index: number) => {
                return (
                  <div key={index} className="Epaper-pageItem">
                    <div>
                      Page - <strong>{index + 1}</strong> |
                    </div>
                    <div style={{ flex: 1 }}>{item?.name}</div>
                    {item?.coords?.length ? (
                      <Tag style={{ fontSize: 9 }}>
                        Cropped ( {item?.coords?.length} - Crop )
                      </Tag>
                    ) : null}
                    {item?.imageUrl ? (
                      <div onClick={() => openModal(item, index)}>
                        <IoReaderOutline color="blue" size={20} />
                      </div>
                    ) : null}
                    <div onClick={() => removeItem(item, index)}>
                      <AiOutlineDelete color="red" size={20} />
                    </div>
                    <div>
                      <CgReorder size={20} />
                    </div>
                    {item?.uploaded ? (
                      item?.imageUrl ? (
                        <FaRegCheckCircle size={18} color="green" />
                      ) : (
                        <MdOutlineBrokenImage
                          size={18}
                          color="orange"
                          onClick={() => {
                            setItem(item);
                            setUpdateImage(true);
                          }}
                        />
                      )
                    ) : props?.uploadIndx === index ? (
                      <Spin
                        indicator={
                          <LoadingOutlined style={{ fontSize: 20 }} spin />
                        }
                      />
                    ) : (
                      <FiUploadCloud size={20} color="orange" />
                    )}
                  </div>
                );
              })}
            </Draggable>
          </div>
        ) : (
          <div className="Epaper-PagesList">
            <div className="Epaper-PagesListBox">
              <IoNewspaperOutline size={30} />
              <div>No Pages Found</div>
            </div>
          </div>
        )}
      </div>
      {updateImage ? (
        <UpdateImage
          open={updateImage}
          data={item}
          editon={props?.edition}
          date={props?.date}
          onCancel={() => setUpdateImage(false)}
          onChange={(val: any) => uploadImage(val, item.index)}
        />
      ) : null}
      {modalVisible ? (
        <CanvasModal
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            setModalImage("");
          }}
          imageUrl={modalImage}
          onSaveClick={(rectangles: any) => onSaveCanvas(rectangles)}
          initialCoords={props?.pages[currentIndex]?.coords}
        />
      ) : null}
    </>
  );
}

export default PagesList;
